import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const RoleContext = createContext<RoleContextValue | null>(null);

export type Role = 'ADMIN' | 'USER' | 'SUPPLIER' | 'STAFF' | 'CONTRACTOR' | (string & {});

interface RoleProviderProps {
	children: ReactNode;
}

interface RoleContextValue {
	isAdmin: boolean;
	isUser: boolean;
	isSupplier: boolean;
	isStaff: boolean;
	isContractor: boolean;
	roles: string[];
	isLoading: boolean;
}

export const RoleProvider = ({ children }: RoleProviderProps) => {
	const { user, isLoading } = useAuth0();

	const value = useMemo<RoleContextValue>(() => {
		const roles = user?.user_roles || [];

		const isAdmin = roles.includes('ADMIN');
		const isSupplier = roles.includes('SUPPLIER');
		const isUser = roles.includes('USER');
		const isStaff = roles.includes('STAFF');
		const isContractor = roles.includes('CONTRACTOR');

		return {
			isAdmin,
			isUser,
			isSupplier,
			isStaff,
			isContractor,
			roles,
			isLoading,
		};
	}, [user, isLoading]);

	return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
	const context = useContext(RoleContext);
	if (!context) {
		throw new Error('useRole must be used within an RoleProvider');
	}
	return context;
};
